import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const PagePermissionsManager = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [pageCategories, setPageCategories] = useState({});

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [pagesRes, usersRes] = await Promise.all([
        api.get('/page-permissions/pages'),
        api.get('/page-permissions/users')
      ]);

      if (pagesRes.data.success) {
        setPages(pagesRes.data.data);
        const categories = {};
        pagesRes.data.data.forEach(page => {
          if (!categories[page.category]) {
            categories[page.category] = [];
          }
          categories[page.category].push(page);
        });
        setPageCategories(categories);
      }

      if (usersRes.data.success) {
        setUsers(usersRes.data.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      const response = await api.get(`/page-permissions/users/${userId}`);
      if (response.data.success) {
        setUserPermissions(response.data.data);
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
      toast.error('Failed to load user permissions');
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    loadUserPermissions(user.id);
  };

  const handlePageSelect = async (page) => {
    setSelectedPage(page);
    try {
      const response = await api.get(`/page-permissions/pages/${page.id}/users`);
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading page users:', error);
      toast.error('Failed to load users for this page');
    }
  };

  const handleTogglePermission = async (userId, pageId, permission) => {
    try {
      const current = userPermissions.find(p => p.page_id === pageId);
      const permissions = {
        can_view: permission === 'can_view' ? !current?.can_view : current?.can_view || false,
        can_edit: permission === 'can_edit' ? !current?.can_edit : current?.can_edit || false,
        can_delete: permission === 'can_delete' ? !current?.can_delete : current?.can_delete || false
      };

      if (!permissions.can_view) {
        permissions.can_edit = false;
        permissions.can_delete = false;
      }

      const response = await api.post(`/page-permissions/users/${userId}/pages/${pageId}`, permissions);

      if (response.data.success) {
        toast.success('Permission updated successfully');
        loadUserPermissions(userId);
      }
    } catch (error) {
      console.error('Error updating permission:', error);
      toast.error('Failed to update permission');
    }
  };

  const handleRevokeAccess = async (userId, pageId) => {
    try {
      const response = await api.delete(`/page-permissions/users/${userId}/pages/${pageId}`);

      if (response.data.success) {
        toast.success('Access revoked successfully');
        if (selectedUser?.id === userId) {
          loadUserPermissions(userId);
        }
      }
    } catch (error) {
      console.error('Error revoking access:', error);
      toast.error('Failed to revoke access');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-2xl font-bold text-gray-900">Page Permissions Management</h2>
        <p className="text-sm text-gray-600 mt-1">Manage which users have access to specific pages</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-8 px-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            Manage Users
          </button>
          <button
            onClick={() => setActiveTab('pages')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pages'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            Manage Pages
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'users' ? (
          <div className="grid grid-cols-12 gap-6">
            {/* Users List */}
            <div className="col-span-12 md:col-span-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Users</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-4">
                {users.map(user => (
                  <button
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedUser?.id === user.id
                        ? 'bg-indigo-100 border-indigo-300 border'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">{user.business_name || user.email}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                    <div className="text-xs text-gray-400">Role: {user.role}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* User Permissions */}
            <div className="col-span-12 md:col-span-8">
              {selectedUser ? (
                <div>
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Permissions for {selectedUser.business_name || selectedUser.email}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {userPermissions.filter(p => p.can_view).length} of {pages.length} pages accessible
                    </p>
                  </div>

                  {/* Permissions by Category */}
                  <div className="space-y-6">
                    {Object.entries(pageCategories).map(([category, categoryPages]) => (
                      <div key={category} className="border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3 capitalize text-sm">
                          {category.replace(/_/g, ' ')}
                        </h4>
                        <div className="space-y-2">
                          {categoryPages.map(page => {
                            const permission = userPermissions.find(p => p.page_id === page.id);
                            return (
                              <div
                                key={page.id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100"
                              >
                                <div>
                                  <div className="font-medium text-sm text-gray-900">{page.display_name}</div>
                                  <div className="text-xs text-gray-500">{page.description}</div>
                                </div>
                                <div className="flex gap-4">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={permission?.can_view || false}
                                      onChange={() => handleTogglePermission(selectedUser.id, page.id, 'can_view')}
                                      className="rounded border-gray-300"
                                    />
                                    <span className="text-xs text-gray-600">View</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={permission?.can_edit || false}
                                      disabled={!permission?.can_view}
                                      onChange={() => handleTogglePermission(selectedUser.id, page.id, 'can_edit')}
                                      className="rounded border-gray-300 disabled:opacity-50"
                                    />
                                    <span className="text-xs text-gray-600">Edit</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={permission?.can_delete || false}
                                      disabled={!permission?.can_view}
                                      onChange={() => handleTogglePermission(selectedUser.id, page.id, 'can_delete')}
                                      className="rounded border-gray-300 disabled:opacity-50"
                                    />
                                    <span className="text-xs text-gray-600">Delete</span>
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Select a user to manage their page permissions</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-6">
            {/* Pages List */}
            <div className="col-span-12 md:col-span-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pages</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-4">
                {pages.map(page => (
                  <button
                    key={page.id}
                    onClick={() => handlePageSelect(page)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedPage?.id === page.id
                        ? 'bg-indigo-100 border-indigo-300 border'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">{page.display_name}</div>
                    <div className="text-xs text-gray-500">{page.route_path}</div>
                    <div className="text-xs text-gray-400 capitalize">{page.category}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Page Details */}
            <div className="col-span-12 md:col-span-8">
              {selectedPage ? (
                <div>
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{selectedPage.display_name}</h3>
                    <p className="text-sm text-gray-600">{selectedPage.description}</p>
                    <div className="mt-4 bg-gray-50 p-3 rounded text-sm text-gray-600">
                      <strong>Route:</strong> {selectedPage.route_path}
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Users with Access</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-900">User</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-900">Email</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-900">Role</th>
                            <th className="px-4 py-2 text-center font-medium text-gray-900">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {users.map(user => (
                            <tr key={user.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2">{user.business_name || 'N/A'}</td>
                              <td className="px-4 py-2 text-gray-600">{user.email}</td>
                              <td className="px-4 py-2">
                                <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  {user.role}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <button
                                  onClick={() => handleRevokeAccess(user.id, selectedPage.id)}
                                  className="text-red-600 hover:text-red-900 text-xs font-medium"
                                >
                                  Revoke
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Select a page to view users with access</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PagePermissionsManager;
